import { useRouter, useSearchParams } from 'next/navigation';
import { join } from 'path';
import { useSmokeballSdkStore } from '@/lib/store/use-smokeball-sdk-store';
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import { useCallback } from 'react';
import { 
  isChatRoute, 
  getBasePath, 
  shouldStripChatParams,
  PRESERVED_CHAT_CONTEXT_PARAMS 
} from '@/lib/utils/chat-navigation-utils';

export const joinPathNames = (basePath: string, subPath: string): string => {
    if (basePath.includes(subPath)) {
        return basePath;
    }
    return join(basePath, subPath);
};

export const useNavigateWithQuery = () => {
    const router = useRouter()
    const searchParams = useSearchParams()
    const sdk = useSmokeballSdkStore((store) => store.sdk)
    const isSdkAvailable = useSmokeballSdkStore((store) => store.isAvailable)
    const isSmokeballWeb = useSmokeballSdkStore((store) => store.isSmokeballWeb)

    // Pushes a new url to the router, optionally preserving/merging any existing searchParams
    const navigateWithQuery = useCallback((
      url: string, 
      opts?: {
        refresh?: boolean
        existingQueryParamsWhitelist?: string[]
        target?: { type: '_blank', title: string }
        prefetch?: boolean
        preserveAllExistingQueryParams?: boolean
        modalHeight?: number
        modalWidth?: number
      }
    ) => {
        let [baseUrl] = url.split('?')
        const [, newQueryString] = url.split('?')

        // When navigating to a new page FROM one of the following routes, strip the chat route params
        // /chat/[id]
        // /home/chat/[id]  
        // /draft/chat/[id]

        // The URL format is /chat/[id]?matterId=...&...
        const shouldStripChat = shouldStripChatParams({ fromPath: window.location.pathname, targetUrl: url })

        let effectiveWhitelist = opts?.existingQueryParamsWhitelist || []

        if (shouldStripChat && isChatRoute(window.location.pathname)) {
          // Convert current chat route to base path
          baseUrl = getBasePath(window.location.pathname)

          // When stripping chat params, preserve essential context parameters
          const preservedParams = PRESERVED_CHAT_CONTEXT_PARAMS
          effectiveWhitelist = [...new Set([...effectiveWhitelist, ...preservedParams])]
        }

        const currentQueryParams = Object.fromEntries(Array.from(searchParams.entries()).filter(
          ([key]) => {
            if (opts?.preserveAllExistingQueryParams) {
              return true
            }
            if (effectiveWhitelist.includes(key)) {
              return true
            }
            return false
          }
        ))

        const newQueryParams = new URLSearchParams(newQueryString)
        newQueryParams.forEach((value, key) => {
            currentQueryParams[key] = value
        })

        const combinedQueryParams = new URLSearchParams(currentQueryParams)

        const newRoute = `${baseUrl}?${combinedQueryParams.toString()}`

        if (opts?.target) {
            const absoluteUrl = new URL(newRoute, window.location.origin).href
            if (isSdkAvailable) {
                if (isSmokeballWeb) {
                    void sdk?.host.modal({ url: absoluteUrl, title: opts.target.title, height: opts.modalHeight, width: opts.modalWidth })
                } else {
                    sdk?.host.open({ url: absoluteUrl, title: opts.target.title, height: opts.modalHeight, width: opts.modalWidth })
                }
            } else {
                router.push(newRoute)
            }
        } else {
            if (opts?.prefetch) {
                router.prefetch(newRoute, { kind: PrefetchKind.FULL })
                setTimeout(() => {
                    router.push(newRoute)
                }, 1000);
            } else {
                router.push(newRoute)
            }
        }
        if (opts?.refresh) {
            router.refresh()
        }
    }, [isSdkAvailable, isSmokeballWeb, sdk, searchParams, router])

    return navigateWithQuery
}
